import SoundManager from '../utils/SoundManager.js';
import Config from '../utils/Config.js';
import Utils from '../utils/Utils.js';
import LocaleManager from '../utils/LocaleManager.js';
import StateButton from '../controls/StateButton.js';

export default class NumericStepper extends Phaser.Group {

    constructor(game, { availableValues, value, maxValue, minValue = 0, step = 1, label, formatFunction, bgImage })
    {
        super(game);

        if (typeof formatFunction !== 'function')
        {
            formatFunction = (num) => '' + num;
        }

        this.params = { availableValues, value, maxValue, minValue, step, label, formatFunction };

        this.onChange = new Phaser.Signal();

        let bg = this._bg = new Phaser.Image(game, 0, 0, bgImage || 'panels.more-less-bg');
        this.addChild(bg);

        let buttonLess = this._buttonLess = new StateButton(game, 0, 0, 'buttons.minus', this._onClickLess, this);
        buttonLess.name = 'buttonLess';
        buttonLess.anchor.set(0.5);
        buttonLess.position.set(
            (buttonLess.hitArea.width >> 1) + 2,
            (bg.height >> 1) + (Config.MobileDevice ? 0 : 1)
        );
        this.addChild(buttonLess);

        let buttonMore = this._buttonMore = new StateButton(game, 0, 0, 'buttons.plus', this._onClickMore, this);
        buttonMore.name = 'buttonMore';
        buttonMore.anchor.set(0.5);
        buttonMore.position.set(
            bg.width - (buttonMore.hitArea.width >> 1) - (Config.MobileDevice ? 0 : 2),
            buttonLess.y
        );
        this.addChild(buttonMore);

        if (Config.MobileDevice)
        {
            buttonLess.hitArea = null;
            buttonMore.hitArea = null;
        }
        else
        {
            buttonLess.hitArea.x = -(buttonLess.width >> 1) + buttonLess.hitArea.x;
            buttonLess.hitArea.y = -(buttonLess.height >> 1) + buttonLess.hitArea.y;

            buttonMore.hitArea.x = -(buttonMore.width >> 1) + buttonMore.hitArea.x;
            buttonMore.hitArea.y = -(buttonMore.height >> 1) + buttonMore.hitArea.y;
        }

        this._defaultValueTextSize = 30;

        let valueText = this._valueText = new Phaser.Text(game, bg.width >> 1, (bg.height >> 1) + 2, '-', {
            font: Config.DEFAULT_FONT,
            fontSize: this._defaultValueTextSize,
            fontWeight: 'bold',
            fill: '#000'
        });
        valueText.anchor.set(0.5);
        this.addChild(valueText);

        let labelText = this._labelText = new Phaser.Text(game, bg.width >> 1, bg.height + 4, LocaleManager.getString(label), {
            font: Config.DEFAULT_FONT,
            fontSize: 16,
            fontWeight: 'normal',
            fill: '#FFFFFF'
        });
        labelText.anchor.set(0.5, 0);
        this.addChild(labelText);

        this.value = value;
    }

    get width()
    {
        return this._bg.width;
    }

    get height()
    {
        return this._bg.height;
    }

    get value()
    {
        return this._value || 0;
    }

    set value(num)
    {
        let formatFunction = this.params.formatFunction;
        let availableValues = this.params.availableValues;
        let effectiveValue = num;
        if (availableValues && availableValues.length)
        {
            let valueIndex = availableValues.indexOf(effectiveValue);
            if (valueIndex === -1)
            {
                effectiveValue = num;
            }
            else
            {
                effectiveValue = availableValues[valueIndex];
            }
        }
        else
        {
            if (effectiveValue > this.params.maxValue)
            {
                effectiveValue = this.params.maxValue;
            }
            else if (effectiveValue < this.params.minValue)
            {
                effectiveValue = this.params.minValue;
            }
        }

        this._valueText.text = formatFunction(effectiveValue);
        this._value = effectiveValue;

        this.adjustValueText();

        this.invalidateButtons();
    }

    setValueFontSize(value)
    {
        this._forceValueFontSize = value;
        this._valueText.fontSize = value;
    }

    adjustValueText()
    {
        return Promise.resolve(this._defaultValueTextSize);

        // for #149
        //return Utils.makeTimeoutPromise(0).then(() =>
        //{
        //    const label = this._valueText;
        //    label.fontSize = this._forceValueFontSize || this._defaultValueTextSize;
        //
        //    const neighbor = this._buttonLess;
        //    const neighborBounds = neighbor.getBounds();
        //    if (neighbor.hitArea)
        //    {
        //        neighborBounds.width = neighbor.hitArea.width - 2;
        //        neighborBounds.height = neighbor.hitArea.height - 2;
        //        neighborBounds.x += neighborBounds.width >> 1;
        //        neighborBounds.y += neighborBounds.height >> 1;
        //    }
        //
        //    for (let step = 0; step < 12; step++)
        //    {
        //        const labelBounds = label.getBounds();
        //        labelBounds.y -= 4;
        //        if (labelBounds.intersects(neighborBounds))
        //        {
        //            label.fontSize = Math.max(10, label.fontSize - 2);
        //        }
        //        else
        //        {
        //            break;
        //        }
        //    }
        //
        //    return Promise.resolve(label.fontSize);
        //});
    }

    invalidateButtons()
    {
        let availableValues = this.params.availableValues;
        if (!this.enabled)
        {
            this._buttonMore.enabled = this._buttonLess.enabled = this.enabled;
        }
        else if (availableValues && availableValues.length)
        {
            let valueIndex = Math.max(0, availableValues.indexOf(this.value));
            this._buttonLess.enabled = valueIndex > 0;
            this._buttonMore.enabled = valueIndex < availableValues.length - 1;
        }
        else
        {
            this._buttonLess.enabled = this.value > this.params.minValue;
            this._buttonMore.enabled = this.value < this.params.maxValue;
        }
    }

    _onClickMore()
    {
        let availableValues = this.params.availableValues;
        let prevValue = this.value;
        if (availableValues && availableValues.length)
        {
            let valueIndex = Math.max(0, availableValues.indexOf(this.value));
            valueIndex = Math.min(valueIndex + 1, availableValues.length - 1);
            this.value = availableValues[valueIndex];
        }
        else
        {
            this.value += this.params.step;
        }

        this.onChange.dispatch(this, this.value, prevValue);
    }

    _onClickLess()
    {
        let availableValues = this.params.availableValues;
        let prevValue = this.value;
        if (availableValues && availableValues.length)
        {
            let valueIndex = Math.max(0, availableValues.indexOf(this.value));
            valueIndex = Math.max(0, valueIndex - 1);
            this.value = availableValues[valueIndex];
        }
        else
        {
            this.value -= this.params.step;
        }

        this.onChange.dispatch(this, this.value, prevValue);
    }

    get enabled()
    {
        return !!this._enabled;
    }

    set enabled(value)
    {
        this._enabled = value;
        this.invalidateButtons();
    }
}